import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Idle lifetime of a settings unlock. After this much inactivity inside
 * settings the surface re-locks and the PIN must be entered again.
 *
 * @since 1.1.0
 */
export const SETTINGS_UNLOCK_TTL_MS = 5 * 60 * 1000;

const UNLOCK_CHECK_INTERVAL_MS = 15 * 1000;

const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

/**
 * Settings unlock session shared by the kiosk hub and the settings surface.
 *
 * The unlock lives in memory only — it is never persisted, so a reload, a
 * restart, or a restored route can never resurrect it.
 *
 * @since 1.1.0
 */
export interface SettingsUnlockSession {
  /** True while a current, unexpired unlock exists. */
  readonly isUnlocked: boolean;
  /**
   * True when a guarded surface turned an unauthenticated visitor away and the
   * hub should offer the PIN prompt.
   */
  readonly isPinRequested: boolean;
  /** Record a successful PIN verification. */
  readonly unlock: () => void;
  /** Drop the unlock (leaving settings, expiry, or an explicit lock). */
  readonly lock: () => void;
  /** Ask the hub to open the PIN prompt, then clear the request. */
  readonly requestPin: () => void;
  /** Acknowledge a handled PIN request. */
  readonly clearPinRequest: () => void;
}

const SettingsUnlockContext = createContext<SettingsUnlockSession | undefined>(
  undefined,
);

/**
 * Provide the in-memory settings unlock session.
 *
 * Wrap both the kiosk hub and the settings surface so a verified unlock is
 * visible to the guard on the settings side.
 *
 * @since 1.1.0
 * @example
 * ```tsx
 * <SettingsUnlockProvider>
 *   <AppRoutes />
 * </SettingsUnlockProvider>
 * ```
 */
export function SettingsUnlockProvider({ children }: { children: ReactNode }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isPinRequested, setIsPinRequested] = useState(false);
  // The sliding deadline lives in a ref, never in state: activity extends the
  // unlock, it is not a state transition. Writing it to state would hand every
  // consumer a new session object on every pointer event — and any consumer
  // effect keyed on the session would re-run its cleanup, which is how the
  // first interaction inside settings used to lock the admin out.
  const lastActivityRef = useRef<number | undefined>(undefined);

  const unlock = useCallback(function unlockSettings() {
    lastActivityRef.current = Date.now();
    setIsUnlocked(true);
    setIsPinRequested(false);
  }, []);

  const lock = useCallback(function lockSettings() {
    lastActivityRef.current = undefined;
    setIsUnlocked(false);
  }, []);

  const requestPin = useCallback(function requestPinPrompt() {
    setIsPinRequested(true);
  }, []);

  const clearPinRequest = useCallback(function clearPinPrompt() {
    setIsPinRequested(false);
  }, []);

  useEffect(
    function expireIdleSettingsUnlock() {
      if (!isUnlocked) return;

      function refreshUnlockDeadline(): void {
        if (lastActivityRef.current === undefined) return;
        lastActivityRef.current = Date.now();
      }

      for (const eventName of INTERACTION_EVENTS) {
        window.addEventListener(eventName, refreshUnlockDeadline, true);
      }

      const interval = window.setInterval(function checkUnlockExpiry() {
        const lastActivity = lastActivityRef.current;
        if (lastActivity === undefined) return;
        if (Date.now() - lastActivity >= SETTINGS_UNLOCK_TTL_MS) {
          lastActivityRef.current = undefined;
          setIsUnlocked(false);
        }
      }, UNLOCK_CHECK_INTERVAL_MS);

      // Registered once per unlocked period, not once per interaction.
      return function stopExpiryWatch() {
        for (const eventName of INTERACTION_EVENTS) {
          window.removeEventListener(eventName, refreshUnlockDeadline, true);
        }
        window.clearInterval(interval);
      };
    },
    [isUnlocked],
  );

  const value = useMemo<SettingsUnlockSession>(
    () => ({
      isUnlocked,
      isPinRequested,
      unlock,
      lock,
      requestPin,
      clearPinRequest,
    }),
    [isUnlocked, isPinRequested, unlock, lock, requestPin, clearPinRequest],
  );

  return (
    <SettingsUnlockContext.Provider value={value}>
      {children}
    </SettingsUnlockContext.Provider>
  );
}

/**
 * Read the settings unlock session, or `undefined` on surfaces that manage
 * their own lock state (the web shell keeps its own `locked` view model).
 *
 * @since 1.1.0
 * @example
 * ```tsx
 * const unlock = useOptionalSettingsUnlock();
 * if (unlock && !unlock.isUnlocked) return null;
 * ```
 */
export function useOptionalSettingsUnlock(): SettingsUnlockSession | undefined {
  return useContext(SettingsUnlockContext);
}
